import { useCallback, useEffect, useMemo, useState } from "react";

import { useCanUseCustomerBranding, useCustomerBrandings } from "../hooks";

import { isAppBrandingComplete, resolveAppBrand } from "./resolve";
import {
  clearAppBrandSelection,
  readAppBrandSelection,
  subscribeToAppBrandSelection,
  writeAppBrandSelection,
} from "./storage";

import type { CustomerBranding } from "../types";

/** Same gate as export branding — one answer, not two. */
export const useCanUseAppBranding = (): boolean => useCanUseCustomerBranding();

export const useAppBranding = () => {
  const enabled = useCanUseAppBranding();
  const [activeId, setActiveId] = useState<string | undefined>(undefined);

  /** Read when the gate flips, not at mount: it waits on the organization role. */
  useEffect(() => {
    if (!enabled) {
      setActiveId(undefined);
      return;
    }
    setActiveId(readAppBrandSelection());
    return subscribeToAppBrandSelection(() =>
      setActiveId(readAppBrandSelection()),
    );
  }, [enabled]);

  const { data: profiles = [], isSuccess } = useCustomerBrandings(enabled);

  /** Costs a flash of the base brand on a cold load, which beats serving one
      that went stale when someone else edited the shared row. A profile that is
      gone or no longer wearable resolves to nothing, so the app falls back on
      its own — the stored id is left alone rather than raced over. */
  const brand = useMemo(() => {
    if (!enabled || !activeId || !isSuccess) return undefined;

    const profile = profiles.find((entry) => entry.id === activeId);
    if (!profile || !isAppBrandingComplete(profile)) return undefined;

    try {
      return resolveAppBrand(profile);
    } catch (error) {
      console.error("Failed to resolve the applied brand:", error);
      return undefined;
    }
  }, [enabled, activeId, isSuccess, profiles]);

  const select = useCallback(
    (profile: CustomerBranding) => {
      if (!enabled) return;
      /** Resolved first, so an unwearable profile is refused here. */
      writeAppBrandSelection(resolveAppBrand(profile).id);
    },
    [enabled],
  );

  const reset = useCallback(() => {
    if (!enabled) return;
    clearAppBrandSelection();
  }, [enabled]);

  return { brand, profiles, select, reset, enabled };
};
